/* eslint-disable react/no-children-count */ // HACK: Don't use Children.count here
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@connected/ui/components/table';
import { cn } from '@connected/ui/lib/utils';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

type ScrollableTableContextType = {
    headerTableRef: React.RefObject<HTMLTableElement | null>;
    bodyTableRef: React.RefObject<HTMLTableElement | null>;
    headerContainerRef: React.RefObject<HTMLDivElement | null>;
    bodyContainerRef: React.RefObject<HTMLDivElement | null>;
    scrollSyncRef: React.RefObject<HTMLDivElement | null>;
    maxHeight: number;
    alignTableColumns: () => void;
    syncHorizontalScroll: (sourceElement: HTMLElement) => void;
    containerIds: { header: string; body: string };
};

const ScrollableTableContext = React.createContext<ScrollableTableContextType | null>(null);

function useScrollableTable() {
    const context = React.use(ScrollableTableContext);
    if (!context) {
        throw new Error('ScrollableTable components must be used within a ScrollableTable');
    }
    return context;
}

type ScrollableTableProps = {
    maxHeight: number;
    children: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>;

function ScrollableTable({ maxHeight, children, className, ...props }: ScrollableTableProps) {
    const headerTableRef = useRef<HTMLTableElement>(null);
    const bodyTableRef = useRef<HTMLTableElement>(null);
    const headerContainerRef = useRef<HTMLDivElement>(null);
    const bodyContainerRef = useRef<HTMLDivElement>(null);
    const scrollSyncRef = useRef<HTMLDivElement>(null);

    // Generate unique IDs for containers
    const containerIds = useRef({
        header: `test-header`,
        body: `test-body`,
    });

    // Synchronize horizontal scrolling between header, body, and sync element, preventing infinite loops
    const isSyncingRef = useRef<null | HTMLElement>(null);
    const lastScrollLeftRef = useRef<{ [key: string]: number }>({});

    const syncHorizontalScroll = useCallback((sourceElement: HTMLElement) => {
        const currentScrollLeft = sourceElement.scrollLeft;

        // Determine which element is the source using refs
        const isHeaderSource = sourceElement === headerContainerRef.current;
        const isBodySource = sourceElement === bodyContainerRef.current;

        if (!isHeaderSource && !isBodySource)
            return;

        const elementId = isHeaderSource ? 'header' : 'body';

        // Only sync if horizontal scroll position actually changed
        if (lastScrollLeftRef.current[elementId] === currentScrollLeft) {
            return;
        }

        if (isSyncingRef.current === sourceElement)
            return;
        isSyncingRef.current = sourceElement;

        lastScrollLeftRef.current[elementId] = currentScrollLeft;

        // Sync to the other container element
        if (headerContainerRef.current && !isHeaderSource) {
            headerContainerRef.current.scrollLeft = currentScrollLeft;
            lastScrollLeftRef.current.header = currentScrollLeft;
        }
        if (bodyContainerRef.current && !isBodySource) {
            bodyContainerRef.current.scrollLeft = currentScrollLeft;
            lastScrollLeftRef.current.body = currentScrollLeft;
        }

        // Allow other scroll events after a tick
        setTimeout(() => {
            isSyncingRef.current = null;
        }, 0);
    }, []);

    // Function to align table columns
    const alignTableColumns = useCallback(() => {
        if (headerTableRef.current && bodyTableRef.current) {
            const headerTable = headerTableRef.current;
            const bodyTable = bodyTableRef.current;

            const headerCells = headerTable.querySelectorAll('thead th');
            const bodyRows = bodyTable.querySelectorAll('tbody tr');

            // If no header cells, exit early
            if (headerCells.length === 0) {
                return;
            }

            // If no body rows, set natural header widths and exit
            if (bodyRows.length === 0) {
                // Reset all styles first
                headerCells.forEach((cell) => {
                    const htmlCell = cell as HTMLElement;
                    htmlCell.style.width = '';
                    htmlCell.style.minWidth = '';
                    htmlCell.style.maxWidth = '';
                });
                headerTable.style.width = '';
                bodyTable.style.width = '';
                return;
            }

            // Reset widths first
            headerCells.forEach((cell) => {
                const htmlCell = cell as HTMLElement;
                htmlCell.style.width = '';
                htmlCell.style.minWidth = '';
                htmlCell.style.maxWidth = '';
            });
            bodyRows.forEach((row) => {
                const cells = row.querySelectorAll('td');
                cells.forEach((cell) => {
                    const htmlCell = cell as HTMLElement;
                    htmlCell.style.width = '';
                    htmlCell.style.minWidth = '';
                    htmlCell.style.maxWidth = '';
                });
            });

            // Reset table widths
            headerTable.style.width = '';
            bodyTable.style.width = '';

            // Force layout recalculation
            void headerTable.offsetWidth;
            void bodyTable.offsetWidth;

            // Get container width
            const containerWidth = headerTable.parentElement?.offsetWidth || headerTable.offsetWidth;

            // Check if scrollbar is present by comparing actual height vs max height
            const maxHeightPx = maxHeight * 16; // Convert rem to pixels (assuming 1rem = 16px)
            const actualContentHeight = bodyTable.scrollHeight;
            const hasScrollbar = actualContentHeight > maxHeightPx;

            const scrollbarBufferWidth = hasScrollbar ? 12 : 0;
            const bodyAvailableWidth = containerWidth - scrollbarBufferWidth;

            // Calculate natural column widths by measuring content across all rows
            const columnWidths: number[] = [];

            // Measure header cell widths
            headerCells.forEach((cell, index) => {
                const headerWidth = (cell as HTMLElement).offsetWidth;
                columnWidths[index] = headerWidth;
            });

            // Measure body cell widths across all rows to find maximum content width per column
            bodyRows.forEach((row) => {
                const cells = row.querySelectorAll('td');
                cells.forEach((cell, index) => {
                    if (index < columnWidths.length) {
                        const cellWidth = (cell as HTMLElement).offsetWidth;
                        columnWidths[index] = Math.max(columnWidths[index] || 0, cellWidth);
                    }
                });
            });

            // Calculate minimum required widths and determine if columns need to be constrained
            const totalNaturalWidth = columnWidths.reduce((sum, width) => sum + width, 0);

            // Additional safety check for empty or invalid column widths
            if (columnWidths.length === 0 || totalNaturalWidth === 0) {
                return;
            }

            let headerFinalWidths: number[] = [];
            let bodyFinalWidths: number[] = [];

            if (totalNaturalWidth <= bodyAvailableWidth) {
                // Content fits naturally, use natural widths
                headerFinalWidths = [...columnWidths];
                bodyFinalWidths = [...columnWidths];
            }
            else {
                // Content exceeds container, apply intelligent proportional scaling

                // Set absolute minimum widths to prevent cells from becoming unreadable
                const absoluteMinWidths = columnWidths.map((width, index) => {
                    // First column (usually names/identifiers): absolute minimum 150px
                    if (index === 0)
                        return Math.max(150, Math.min(200, width));
                    // Last column (usually dates): absolute minimum 100px
                    if (index === columnWidths.length - 1)
                        return Math.max(100, Math.min(120, width));
                    // Middle columns: absolute minimum 80px
                    return Math.max(80, Math.min(100, width));
                });

                const totalAbsoluteMinWidth = absoluteMinWidths.reduce((sum, width) => sum + width, 0);

                // If even absolute minimums don't fit, enable horizontal scrolling
                if (totalAbsoluteMinWidth > bodyAvailableWidth) {
                    // Use absolute minimum widths and let table overflow horizontally
                    bodyFinalWidths = [...absoluteMinWidths];
                }
                else {
                    // Normal scaling logic when we have enough space for minimums
                    const remainingWidth = bodyAvailableWidth - totalAbsoluteMinWidth;

                    if (remainingWidth > 0) {
                        // Distribute remaining width proportionally based on content preference
                        const preferenceWeights = columnWidths.map((width, index) => {
                            // First column gets higher weight (more important content)
                            if (index === 0)
                                return width * 1.5;
                            // Last column gets medium weight
                            if (index === columnWidths.length - 1)
                                return width * 1.0;
                            // Middle columns get lower weight
                            return width * 0.7;
                        });

                        const totalWeight = preferenceWeights.reduce((sum, weight) => sum + weight, 0);

                        // Protect against division by zero
                        if (totalWeight === 0) {
                            bodyFinalWidths = [...absoluteMinWidths];
                        }
                        else {
                            bodyFinalWidths = absoluteMinWidths.map((minWidth, index) => {
                                const extraWidth = (preferenceWeights[index] / totalWeight) * remainingWidth;
                                return Math.floor(minWidth + extraWidth);
                            });
                        }
                    }
                    else {
                        // Use absolute minimum widths
                        bodyFinalWidths = [...absoluteMinWidths];
                    }
                }

                // Header calculation: add scrollbar buffer to maintain alignment
                if (hasScrollbar) {
                    // Add the scrollbar buffer to the last header column for proper alignment
                    headerFinalWidths = [...bodyFinalWidths];
                    if (headerFinalWidths.length > 0) {
                        headerFinalWidths[headerFinalWidths.length - 1] += scrollbarBufferWidth;
                    }
                }
                else {
                    // No scrollbar, header and body use same widths
                    headerFinalWidths = [...bodyFinalWidths];
                }
            }

            // Apply calculated widths to header table
            headerCells.forEach((cell, index) => {
                const htmlCell = cell as HTMLElement;
                const width = `${headerFinalWidths[index]}px`;
                htmlCell.style.width = width;
                htmlCell.style.minWidth = width;
                htmlCell.style.maxWidth = width;
            });

            // Apply calculated widths to body table
            bodyRows.forEach((row) => {
                const cells = row.querySelectorAll('td');
                cells.forEach((cell, index) => {
                    if (index < bodyFinalWidths.length) {
                        const htmlCell = cell as HTMLElement;
                        const width = `${bodyFinalWidths[index]}px`;
                        htmlCell.style.width = width;
                        htmlCell.style.minWidth = width;
                        htmlCell.style.maxWidth = width;
                    }
                });
            });

            // Set table widths
            const totalHeaderWidth = headerFinalWidths.reduce((sum, width) => sum + width, 0);
            const totalBodyWidth = bodyFinalWidths.reduce((sum, width) => sum + width, 0);

            // Safety check for valid widths before applying
            if (totalHeaderWidth > 0 && totalBodyWidth > 0
                && Number.isFinite(totalHeaderWidth) && Number.isFinite(totalBodyWidth)
                && totalHeaderWidth < 1000000 && totalBodyWidth < 1000000) {
                headerTable.style.width = `${totalHeaderWidth}px`;
                bodyTable.style.width = `${totalBodyWidth}px`;
            }
        }
    }, [maxHeight]);

    // Effect to align on window resize
    useEffect(() => {
        const handleResize = () => {
            alignTableColumns();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [alignTableColumns]);

    // Effect to add scroll event listeners using refs
    useEffect(() => {
        const headerContainer = headerContainerRef.current;
        const bodyContainer = bodyContainerRef.current;

        const handleHeaderScroll = (e: Event) => {
            syncHorizontalScroll(e.target as HTMLElement);
        };

        const handleBodyScroll = (e: Event) => {
            syncHorizontalScroll(e.target as HTMLElement);
        };

        if (headerContainer) {
            headerContainer.addEventListener('scroll', handleHeaderScroll, { passive: false });
        }
        if (bodyContainer) {
            bodyContainer.addEventListener('scroll', handleBodyScroll, { passive: false });
        }

        // Cleanup
        return () => {
            if (headerContainer) {
                headerContainer.removeEventListener('scroll', handleHeaderScroll);
            }
            if (bodyContainer) {
                bodyContainer.removeEventListener('scroll', handleBodyScroll);
            }
        };
    }, [syncHorizontalScroll]);

    const contextValue: ScrollableTableContextType = useMemo(() => ({
        headerTableRef,
        bodyTableRef,
        headerContainerRef,
        bodyContainerRef,
        scrollSyncRef,
        maxHeight,
        alignTableColumns,
        syncHorizontalScroll,
        containerIds: containerIds.current,
    }), [alignTableColumns, maxHeight, syncHorizontalScroll]);

    return (
        <ScrollableTableContext value={contextValue}>
            <div className={cn('w-full', className)} {...props}>
                {children}
            </div>
        </ScrollableTableContext>
    );
}

function ScrollableTableHeader({ ref, className, children, ...props }: React.ComponentProps<typeof Table> & { ref?: React.RefObject<HTMLTableElement | null> }) {
    const { headerTableRef, headerContainerRef } = useScrollableTable();

    // Suppress unused ref warning
    void ref;

    return (
        <div
            ref={headerContainerRef}
            className="relative w-full overflow-auto"
            style={{
                scrollbarWidth: 'none', /* Firefox */
                msOverflowStyle: 'none', /* IE and Edge */
                // WebkitScrollbar: 'none' /* Webkit browsers */
            }}
        >
            <Table
                containerRef={headerTableRef}
                className={cn('rounded-2xl', isFirefox ? '' : 'table-fixed', className)}
                style={{ ...(isFirefox && { tableLayout: 'auto' }) }}
                {...props}
            >
                <TableHeader className="bg-background-secondary sticky top-0 z-10">
                    {children}
                </TableHeader>
            </Table>
        </div>
    );
}
ScrollableTableHeader.displayName = 'ScrollableTableHeader';

function ScrollableTableBody({ ref, className, children, ...props }: React.ComponentProps<typeof Table> & { ref?: React.RefObject<HTMLTableElement | null> }) {
    const { bodyTableRef, bodyContainerRef, maxHeight, alignTableColumns } = useScrollableTable();

    // Suppress unused ref warning
    void ref;

    // Effect to align columns after content changes
    useEffect(() => {
        // Only align columns if there are actual children to render
        if (children && React.Children.count(children) > 0) {
            // Use a timeout to ensure the DOM has been updated
            const timeoutId = setTimeout(() => {
                alignTableColumns();
            }, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [children, alignTableColumns]);

    return (
        <div
            ref={bodyContainerRef}
            className="relative w-full overflow-auto"
            style={{ maxHeight: `${maxHeight}rem` }}
        >
            <Table
                containerRef={bodyTableRef}
                className={cn('rounded-2xl', isFirefox ? '' : 'table-fixed', className)}
                style={{ ...(isFirefox && { tableLayout: 'auto' }) }}
                {...props}
            >
                <TableBody>
                    {children}
                </TableBody>
            </Table>
        </div>
    );
}
ScrollableTableBody.displayName = 'ScrollableTableBody';

// Re-export existing components for consistency
const ScrollableTableRow = TableRow;
const ScrollableTableHead = TableHead;
const ScrollableTableCell = TableCell;

export {
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableHeader,
    ScrollableTableRow,
};
